import { useMemo } from 'react';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7965406aa664502cb6a3888418e2a788.jpg';

export default function PrivacyPolicyPage() {
  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy | Work Play Save',
    description: 'Read the Work Play Save privacy policy to understand how we collect, use, and protect your data when you use our website.',
    url: `${SITE_URL}/privacy-policy`,
    publisher: {
      '@type': 'Organization',
      name: 'Work Play Save',
      url: SITE_URL,
    },
    dateModified: '2026-03-18',
  }), []);

  return (
    <>
      <title>Privacy Policy | Work Play Save</title>
      <meta name="description" content="Read the Work Play Save privacy policy to understand how we collect, use, and protect your data when you use our website." />
      <meta name="keywords" content="privacy policy, data protection, user privacy, website privacy, data usage policy, personal information protection, privacy terms" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/privacy-policy`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Privacy Policy | Work Play Save" />
      <meta property="og:description" content="Read the Work Play Save privacy policy to understand how we collect, use, and protect your data when you use our website." />
      <meta property="og:url" content={`${SITE_URL}/privacy-policy`} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Privacy Policy | Work Play Save" />
      <meta name="twitter:description" content="Read the Work Play Save privacy policy to understand how we collect, use, and protect your data when you use our website." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLd data={webPageSchema} />
      <div className="min-h-screen">
        {/* Header */}
        <header className="bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
          <div className="max-w-4xl mx-auto px-6 py-16">
            <div className="text-center space-y-4">
              <h1 className="text-5xl font-bold text-white uppercase tracking-tight">
                Privacy Policy
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mt-4"></div>
              </h1>
              <p className="text-gray-400">Last updated: January 2025</p>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Introduction</h2>
              <p className="text-lg">
                Work Play Save ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Information We Collect</h2>
              <p className="text-lg">
                We may collect the following types of information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>Email addresses provided through newsletter subscriptions</li>
                <li>Usage data and analytics (pages visited, time spent, device information)</li>
                <li>Cookies and tracking technologies for site functionality and analytics</li>
              </ul>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">How We Use Your Information</h2>
              <p className="text-lg">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>Send newsletters and product recommendations (if you subscribe)</li>
                <li>Improve website functionality and user experience</li>
                <li>Analyze site traffic and usage patterns</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Cookies and Tracking</h2>
              <p className="text-lg">
                We use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Third-Party Services</h2>
              <p className="text-lg">
                We may use third-party services for analytics, advertising, and affiliate tracking. These services may collect information about your browsing activity. We are not responsible for the privacy practices of third-party websites.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Data Security</h2>
              <p className="text-lg">
                We implement reasonable security measures to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Your Rights</h2>
              <p className="text-lg">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>Unsubscribe from our newsletter at any time</li>
                <li>Request access to your personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Opt out of certain data collection practices</li>
              </ul>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Changes to This Policy</h2>
              <p className="text-lg">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Contact Us</h2>
              <p className="text-lg">
                If you have questions about this Privacy Policy, please contact us through our website.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}